import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getAdminUsers } from './getAdminUsers'
import { UserListItem, UserListResponse, UserListRequestPayload } from './types'
import { QUERY_KEY_SETTINGS_ADMIN_PANEL, SETTINGS_QUERY_ALWAYS_REVALIDATE } from '@/lib/api/constants'

type Props = {
  companyId: string
  /** `take` to rozmiar strony; `skip` wylicza się z doczytanych stron. */
  payload: UserListRequestPayload
}

/** Lista użytkowników doczytywana stronami przy przewijaniu. Klucz jak w `useQueryAdminUsers`, więc mutacje go unieważniają. */
export function useInfiniteQueryAdminUsers({ companyId, payload }: Props) {
  const query = useInfiniteQuery<UserListResponse>({
    queryKey: [QUERY_KEY_SETTINGS_ADMIN_PANEL, 'users', companyId, payload],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await getAdminUsers({ companyId, payload: { ...payload, skip: pageParam as number } })
      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + (page.users?.length ?? 0), 0)
      return (lastPage.users?.length ?? 0) > 0 && loaded < (lastPage.usersCount ?? lastPage.totalCount ?? 0) ? loaded : undefined
    },
    ...SETTINGS_QUERY_ALWAYS_REVALIDATE,
  })

  const users: UserListItem[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.users ?? []) ?? [],
    [query.data],
  )

  return { ...query, users }
}
